import { Paper, Typography } from '@mui/material';
import { format } from 'date-fns';
import moment from 'moment';
import {
  AutocompleteInput,
  Datagrid,
  DateField,
  Filter,
  NumberField,
  NumberInput,
  ReferenceField,
  ReferenceInput,
  TextField,
  useListContext,
} from 'react-admin';
import { Permission, ReportList } from "../../components";
import { BetweenDatesInput } from '../../components/BetweenDatesInput';
import ListPagination from '../../components/ListPagination';
import { PERMISSIONS } from "../../constants";
import { filterToQuery } from "../../utils";

const containerSx = {
  margin: '16px',
  padding: '16px'
};

const titleSx = {
  margin: '20px 0 15px 0',
  padding: '10px 0',
  fontWeight: 'bold',
  fontSize: '1.1rem',
  borderBottom: '2px solid #1976d2'
};

const subtitleSx = {
  margin: '10px 0',
  color: '#666'
};

const ListStationsWithoutBills = () => {
  const props = useListContext();

  if (!props.data || props.data.length === 0) {
    return (
      <Typography sx={subtitleSx}>
        Nenhum posto encontrado sem boleto para o período selecionado.
      </Typography>
    );
  }

  const reportData = props.data[0];
  if (!reportData || !reportData.data || reportData.data.length === 0) {
    return (
      <Typography sx={subtitleSx}>
        Nenhum posto encontrado sem boleto para o período selecionado.
      </Typography>
    );
  }

  return (
    <Paper sx={containerSx} elevation={1}>
      {reportData.data.map((monthGroup, index) => (
        <div key={monthGroup.monthDate}>
          <Typography sx={titleSx}>
            Mês: {monthGroup.monthDate}
          </Typography>
          <Datagrid
            bulkActionButtons={false}
            data={monthGroup.stations}
            total={monthGroup.stations.length}
          >
            <ReferenceField
              label="Rede"
              source="chainId"
              reference="chains"
              link={false}
              sortable={false}
            >
              <TextField source="name" />
            </ReferenceField>
            <ReferenceField
              label="Posto"
              source="placeId"
              reference="stations"
              link="show"
              sortable={false}
            >
              <TextField source="name" />
            </ReferenceField>
            <ReferenceField
              label="CNPJ"
              source="placeId"
              reference="stations"
              link={false}
              sortable={false}
            >
              <TextField source="cnpj" />
            </ReferenceField>
            <NumberField
              source="fillinCount"
              label="Abastecimentos"
              textAlign="center"
              sortable={false}
            />
            <DateField
              source="billingStartDate"
              label="Início Faturamento"
              textAlign="center"
              emptyText="Não configurado"
              locales="pt-BR"
              sortable={false}
            />
            <NumberField
              source="gracePeriod"
              label="Carência (meses)"
              textAlign="center"
              emptyText="Não configurado"
              sortable={false}
            />
            <TextField
              source="gestaoClickId"
              label="Gestão Click ID"
              textAlign="center"
              emptyText="Não mapeado"
              sortable={false}
            />
          </Datagrid>
        </div>
      ))}
    </Paper>
  );
};

const defaultFrom = moment().subtract(1, 'month').startOf('month').toISOString();
const defaultTo = moment().subtract(1, 'month').endOf('month').toISOString();

const ListFilters = props => {
  // filterValues vem do contexto da lista: o Filter é montado pelo ListToolbar e nem sempre recebe
  // os valores por prop.
  const { filterValues } = useListContext();

  return (
    <Filter {...props}>
      <BetweenDatesInput
        alwaysOn
        defaultFrom={defaultFrom}
        defaultTo={defaultTo}
      />
      <NumberInput
        source="minFillinCount"
        label="Min. Abastecimentos"
        min={1}
        alwaysOn
      />
      <ReferenceInput source="chainId" reference="chains" sort={{ field: 'name', order: 'ASC' }} perPage={null} alwaysOn>
        <AutocompleteInput filterToQuery={filterToQuery} label="Rede" optionText="name" />
      </ReferenceInput>
      <ReferenceInput
        source="placeId"
        reference="stations"
        sort={{ field: 'name', order: 'ASC' }}
        perPage={null}
        // Com rede selecionada, a lista de postos vem só dela.
        filter={filterValues?.chainId ? { chainId: filterValues.chainId } : undefined}
        alwaysOn
      >
        <AutocompleteInput filterToQuery={filterToQuery} label="Posto" optionText="name" />
      </ReferenceInput>
    </Filter>
  );
};

const stationsWithoutBillsExport = async (data, fetchRelatedRecords) => {
  if (!data || data.length === 0 || !data[0].data) {
    return [];
  }

  const reportData = data[0].data;

  // Flatten: each station becomes a row with monthDate
  const flatData = reportData.flatMap(monthGroup =>
    monthGroup.stations.map(station => ({
      ...station,
      monthDate: monthGroup.monthDate
    }))
  );

  if (flatData.length === 0) {
    return [];
  }

  // Fetch related data (name, cnpj from stations and chain name)
  const relations = [
    { field: 'placeId', resource: 'stations' },
    { field: 'chainId', resource: 'chains' }
  ];
  const fetchData = await fetchRelatedRecords(relations, flatData);
  const stations = fetchData.find(r => r.resource === 'stations')?.results || [];
  const chains = fetchData.find(r => r.resource === 'chains')?.results || [];

  // Map to export format
  return flatData.map(item => {
    const station = stations.find(s => s.id === item.placeId);
    const chain = chains.find(c => c.id === item.chainId);
    return {
      'Mês': item.monthDate,
      'Rede': chain?.name || '',
      'Posto': station?.name || '',
      'CNPJ': station?.cnpj || '',
      'Abastecimentos': item.fillinCount,
      'Início Faturamento': item.billingStartDate ? format(new Date(item.billingStartDate), 'dd/MM/yyyy') : 'Não configurado',
      'Carência (meses)': item.gracePeriod ?? 'Não configurado',
      'Gestão Click ID': item.gestaoClickId || 'Não mapeado'
    };
  });
};

const ReportStationsWithoutBills = props => {
  const filterValues = {
    from: moment().subtract(1, 'month').startOf('month').toISOString(),
    to: moment().subtract(1, 'month').endOf('month').toISOString(),
    minFillinCount: 5
  };

  return (
    <Permission permission={PERMISSIONS.STATIONS_WITHOUT_BILLS_REPORT}>
      <ReportList periodRequired
        {...props}
        filterDefaultValues={filterValues}
        basePath="report-stations-without-bills"
        resource="reports/stations-without-bills"
        title="Postos sem Boleto"
        filters={<ListFilters />}
        perPage={25}
        pagination={<ListPagination />}
        titleOnPrint="Relatório de Postos sem Boleto"
        customExporter={stationsWithoutBillsExport}
        showPagination={false}
      >
        <ListStationsWithoutBills />
      </ReportList>
    </Permission>
  );
};

export default ReportStationsWithoutBills;
